import { useEffect, useRef, useState } from "react";
import { getTheme } from "../../components/Terminal/themes";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";

interface UseTerminalProps {
  isDarkMode: boolean;
}

export const useTerminal = ({ isDarkMode }: UseTerminalProps) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const [isTerminalReady, setIsTerminalReady] = useState(false);

  // Create terminal
  useEffect(() => {
    let fitTimer: ReturnType<typeof setTimeout> | undefined;
    const resizeTimers = new Set<ReturnType<typeof setTimeout>>();
    let handleResize: (() => void) | undefined;
    let resizeObserver: ResizeObserver | undefined;

    // Use a timeout to ensure the DOM element is ready
    const timer = setTimeout(() => {
      if (!terminalRef.current || xtermRef.current) return;

      const savedTheme = localStorage.getItem("terminal-theme");
      const isInitialDark = savedTheme
        ? savedTheme === "dark"
        : window.matchMedia("(prefers-color-scheme: dark)").matches;

      const term = new Terminal({
        cursorBlink: true,
        theme: getTheme(isInitialDark),
        fontSize: 14,
        lineHeight: 1.2,
        fontFamily:
          '"JetBrains Mono", "SF Mono", "Monaco", "Inconsolata", "Fira Code", "Cascadia Code", monospace',
        fontWeight: "normal",
        fontWeightBold: "bold",
        convertEol: true,
        disableStdin: false,
        allowProposedApi: true,
      });

      term.open(terminalRef.current);
      xtermRef.current = term;
      setIsTerminalReady(true);

      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      fitAddonRef.current = fitAddon;

      fitTimer = setTimeout(() => {
        fitAddon.fit();
        // Auto-focus terminal on load
        term.focus();
      }, 100);

      // Handle window resize
      handleResize = () => {
        if (fitAddonRef.current && xtermRef.current) {
          const resizeTimer = setTimeout(() => {
            resizeTimers.delete(resizeTimer);
            fitAddonRef.current?.fit();
          }, 50);
          resizeTimers.add(resizeTimer);
        }
      };
      window.addEventListener("resize", handleResize);

      resizeObserver = new ResizeObserver(() => {
        handleResize?.();
      });

      resizeObserver.observe(terminalRef.current);
    }, 0);

    return () => {
      clearTimeout(timer);
      clearTimeout(fitTimer);
      resizeTimers.forEach((resizeTimer) => {
        clearTimeout(resizeTimer);
      });
      if (handleResize) {
        window.removeEventListener("resize", handleResize);
      }
      resizeObserver?.disconnect();
      if (xtermRef.current) {
        xtermRef.current.dispose();
        xtermRef.current = null;
      }
      if (fitAddonRef.current) {
        fitAddonRef.current = null;
      }
      setIsTerminalReady(false);
    };
  }, []);

  // Handle theme changes
  useEffect(() => {
    if (!xtermRef.current) return;
    xtermRef.current.options.theme = getTheme(isDarkMode);
  }, [isDarkMode]);

  return {
    terminalRef,
    xtermRef,
    fitAddonRef,
    isTerminalReady,
  };
};
